<template>
  <div class="min-h-screen landing-page">
    <SimpleSeoHead
      :title="seoTitle"
      :description="seoDescription"
      :keywords="seoKeywords"
      :schema="landingPageSchema"
      canonicalUrl="https://coolliving.io"
    />

    <HeroLanding />

    <section id="profiles-section" class="bg-gray-950">
      <FeaturesAndAudience />
    </section>

    <section id="solutions-section">
      <FeatureLanding />
    </section>

    <div id="partner-services-section">
      <PartnerServicesSection />
    </div>

    <CallRequestSection />

    <ColivingNaturalSearchWidget />
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePageTracking } from '@/composables/usePageTracking'
import SimpleSeoHead from './SimpleSeoHead.vue'
import HeroLanding from './HeroLanding.vue'
import FeatureLanding from './FeatureLanding.vue'
import FeaturesAndAudience from './FeaturesAndAudience.vue'
import ColivingNaturalSearchWidget from './ColivingNaturalSearchWidget.vue'
import PartnerServicesSection from './PartnerServicesSection.vue'
import CallRequestSection from './CallRequestSection.vue'
import { getWebsiteSchema, getServiceSchema, getFAQSchema, getSoftwareApplicationSchema } from '../components/schemas/WebsiteSchema.js'

const { t } = useI18n()

usePageTracking('landing')

onMounted(() => {
  const handleScroll = () => {
    const parallaxBgs = document.querySelectorAll('.parallax-bg')
    parallaxBgs.forEach((el) => {
      const speed = parseFloat(el.dataset.speed) || 0.5
      const parentRect = el.parentElement.getBoundingClientRect()

      if (parentRect.top < window.innerHeight && parentRect.bottom > 0) {
        const elementCenter = parentRect.top + parentRect.height / 2
        const viewportCenter = window.innerHeight / 2
        const distanceFromCenter = elementCenter - viewportCenter
        const yPos = -distanceFromCenter * speed * 0.15
        el.style.transform = `translateY(${yPos}px)`
      }
    })
  }

  handleScroll()
  window.addEventListener('scroll', handleScroll, { passive: true })
  window.addEventListener('resize', handleScroll, { passive: true })

  onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll)
    window.removeEventListener('resize', handleScroll)
  })
})

const seoTitle = computed(() => t('landingPageSeo.metaTitle'))
const seoDescription = computed(() => t('landingPageSeo.metaDescription'))

const seoKeywords = 'coliving españa, buscar coliving, coliving madrid, coliving barcelona, software gestión coliving, leads coliving operadores, saas coliving, marketplace coliving, conseguir inquilinos coliving, coliving management, vivir en coliving'

const landingPageSchema = computed(() => {
  const siteUrl = import.meta.env?.VITE_SITE_URL || window.location.origin

  const websiteSchema = getWebsiteSchema(siteUrl)
  const serviceSchema = getServiceSchema(siteUrl)
  const softwareSchema = getSoftwareApplicationSchema(siteUrl)

  const faqSchema = getFAQSchema([
    {
      question: t('landingPageSeo.faq.marketplace.question'),
      answer: t('landingPageSeo.faq.marketplace.answer'),
    },
    {
      question: t('landingPageSeo.faq.software.question'),
      answer: t('landingPageSeo.faq.software.answer'),
    },
    {
      question: t('landingPageSeo.faq.leads.question'),
      answer: t('landingPageSeo.faq.leads.answer'),
    },
    {
      question: t('landingPageSeo.faq.pricing.question'),
      answer: t('landingPageSeo.faq.pricing.answer'),
    },
  ])

  return [...websiteSchema, ...serviceSchema, softwareSchema, faqSchema]
})
</script>

<style scoped>
html {
  scroll-behavior: smooth;
}

.parallax-bg {
  will-change: transform;
  transition: transform 0.15s ease-out;
}

:deep(#features-grid) section {
  padding-left: 0.5rem !important;
  padding-right: 0.5rem !important;
}

:deep(#features-grid) .max-w-6xl {
  padding-left: 0.5rem !important;
  padding-right: 0.5rem !important;
}

@media (max-width: 639px) {
  :deep(#features-grid) .px-6 {
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
  }
}
</style>
